import { registerInstrumentations } from '@opentelemetry/instrumentation'
import { DocumentLoadInstrumentation } from '@opentelemetry/instrumentation-document-load'
import { FetchInstrumentation } from '@opentelemetry/instrumentation-fetch'
import { resourceFromAttributes } from '@opentelemetry/resources'
import {
  ATTR_SERVICE_NAME,
  ATTR_SERVICE_NAMESPACE,
  ATTR_SERVICE_VERSION,
} from '@opentelemetry/semantic-conventions'
import { ATTR_DEPLOYMENT_ENVIRONMENT_NAME } from '@opentelemetry/semantic-conventions/incubating'
import { OTLPTraceExporter } from '@opentelemetry/exporter-trace-otlp-http'
import {
  BatchSpanProcessor,
  ParentBasedSampler,
  TraceIdRatioBasedSampler,
} from '@opentelemetry/sdk-trace-base'
import { WebTracerProvider } from '@opentelemetry/sdk-trace-web'
import { escapeRegExp } from '@/lib/utils'
import pkg from '../../package.json'

const SERVICE_NAME = 'client-dashboard' as const
const SERVICE_NAMESPACE = 'em-semi' as const

let initialized = false

function isTracingEnabled(): boolean {
  const v = import.meta.env.VITE_OTEL_TRACING_ENABLED
  if (v === 'false') return false
  if (v === 'true') return true
  // Opt-in only: when the OTLP collector is not running, the SDK still issues up to 5 HTTP
  // attempts per export (@opentelemetry/otlp-exporter-base MAX_ATTEMPTS), which floods the
  // Network tab with failed /v1/traces calls (e.g. ERR_CONNECTION_REFUSED).
  return false
}

/**
 * Resolves the OTLP HTTP traces URL. In development, falls back to a local collector
 * when `VITE_OTEL_EXPORTER_OTLP_ENDPOINT` is unset. In production builds, an explicit
 * endpoint is required when tracing is on — see README in this folder.
 */
function getTracesEndpoint(): string | null {
  const raw = import.meta.env.VITE_OTEL_EXPORTER_OTLP_ENDPOINT?.replace(/\/$/, '')
  if (raw) {
    if (raw.endsWith('/v1/traces')) return raw
    // Dev often shares the same host; Python defaults to gRPC :4317 — use HTTP :4318 for browser.
    if (raw.includes(':4317')) {
      return `${raw.replace(':4317', ':4318')}/v1/traces`
    }
    return `${raw}/v1/traces`
  }

  if (import.meta.env.PROD) {
    return null
  }

  return 'http://localhost:4318/v1/traces'
}

function getSampleRatio(): number {
  const raw = import.meta.env.VITE_OTEL_TRACES_SAMPLE_RATIO
  if (raw !== undefined && raw !== '') {
    const n = Number(raw)
    if (!Number.isNaN(n) && n >= 0 && n <= 1) return n
  }
  return import.meta.env.PROD ? 0.1 : 1
}

/** URLs that should receive `traceparent` on cross-origin fetch (CORS must allow the header on the API). */
function buildPropagateTraceHeaderCorsUrls(): RegExp | RegExp[] {
  const apiBase = import.meta.env.VITE_API_BASE_URL
  if (apiBase) {
    try {
      const u = new URL(apiBase)
      return new RegExp(`^${escapeRegExp(u.origin)}`)
    } catch {
      /* ignore invalid URL */
    }
  }
  return /^https?:\/\//
}

function buildOtlpIgnoreUrls(tracesEndpoint: string): Array<string | RegExp> {
  const out: Array<string | RegExp> = [/\/v1\/traces(\?|$)/]
  try {
    const u = new URL(tracesEndpoint)
    out.push(new RegExp(`^${escapeRegExp(u.origin)}.*`))
  } catch {
    /* ignore */
  }
  return out
}

export function initClientObservability(): void {
  if (initialized) return
  if (!isTracingEnabled()) return

  try {
    const tracesEndpoint = getTracesEndpoint()
    if (tracesEndpoint === null) {
      console.error(
        '[observability] Tracing is enabled but VITE_OTEL_EXPORTER_OTLP_ENDPOINT is not set. Production builds cannot use the localhost default from the browser. Set a public OTLP HTTP URL (see src/observability/README.md). OpenTelemetry is disabled.',
      )
      return
    }

    const deploymentEnvironment = import.meta.env.DEV
      ? 'local'
      : import.meta.env.MODE === 'production'
        ? 'production'
        : import.meta.env.MODE

    const resourceAttrs: Record<string, string> = {
      [ATTR_SERVICE_NAME]: SERVICE_NAME,
      [ATTR_SERVICE_NAMESPACE]: SERVICE_NAMESPACE,
      [ATTR_SERVICE_VERSION]: pkg.version,
      [ATTR_DEPLOYMENT_ENVIRONMENT_NAME]: deploymentEnvironment,
    }

    const exporter = new OTLPTraceExporter({ url: tracesEndpoint })
    const sampler = new ParentBasedSampler({
      root: new TraceIdRatioBasedSampler(getSampleRatio()),
    })

    const provider = new WebTracerProvider({
      resource: resourceFromAttributes(resourceAttrs),
      sampler,
      spanProcessors: [new BatchSpanProcessor(exporter)],
    })

    provider.register()

    registerInstrumentations({
      instrumentations: [
        new DocumentLoadInstrumentation(),
        new FetchInstrumentation({
          propagateTraceHeaderCorsUrls: buildPropagateTraceHeaderCorsUrls(),
          ignoreUrls: buildOtlpIgnoreUrls(tracesEndpoint),
        }),
      ],
    })

    // BatchSpanProcessor already flushes on document visibilitychange + pagehide (browser build).

    initialized = true
  } catch (error) {
    console.error('Failed to initialize observability:', error)
  }
}

initClientObservability()
